import { useCallback, useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { coverageTranscript } from './api'
import type { CoverageTranscript } from './api'

/** Slow: transcription takes minutes, and nobody watches this tab waiting for a word. */
const POLL_MS = 15000

/**
 * What was said at an event.
 *
 * ## Why coverage needs this more than meetings do
 *
 * A meeting transcript is a convenience — the people in the room already know what was
 * said. A rally transcript is a record: it is the candidate speaking in public, it is what
 * will be quoted back at them, and the alternative is somebody scrubbing four hours of
 * footage for a sentence they half remember.
 *
 * ## The speaker is a CAMERA, not a person
 *
 * A meeting records one track per participant, so each line can carry a name. An event
 * cannot: a camera is a phone somebody is holding, and whoever walks in front of it is on
 * that feed. Naming the feed is the honest answer. Putting a person's name on words they
 * may not have said is exactly the error a campaign cannot afford to make from its own
 * records — so this panel never does, however much nicer it would read.
 *
 * ## Every state says something different
 *
 * "Not recorded", "queued", "running" and "failed" are four different things to tell
 * somebody who is waiting, and a single empty state would tell them none of it.
 */
export function CoverageTranscriptPanel({ eventId }: { eventId: string }) {
  const [transcript, setTranscript] = useState<CoverageTranscript | null>(null)
  const [error, setError] = useState<string | null>(null)

  const pull = useCallback(() => {
    coverageTranscript(eventId).then(setTranscript).catch((e) => setError(explain(e)))
  }, [eventId])

  useEffect(() => {
    pull()
    const t = setInterval(pull, POLL_MS)
    return () => clearInterval(t)
  }, [pull])

  if (error) return <ErrorStrip message={error} />
  if (!transcript) {
    return <p className="py-4 text-center text-erp-xs text-muted-foreground">Loading…</p>
  }

  if (transcript.lines.length === 0) {
    return <Waiting state={transcript.stateCode} reason={transcript.reason} />
  }

  return (
    <div className="space-y-3">
      {transcript.language && (
        <p className="text-erp-xs text-muted-foreground">
          Detected language: {transcript.language}. Machine-made, and worth reading as a
          guide to the recording rather than as a record of it — check the footage before
          quoting anybody.
        </p>
      )}
      <ul className="space-y-2">
        {transcript.lines.map((line, i) => (
          <li key={`${line.recordingId}-${line.startMs}-${i}`} className="text-erp-sm">
            <span className="mr-2 font-medium">{line.camera}</span>
            <span className="text-erp-xs text-muted-foreground">{clock(line.startMs)}</span>
            <p className="whitespace-pre-wrap break-words">{line.text}</p>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Waiting({ state, reason }: { state: string; reason: string | null }) {
  switch (state) {
    /*
       THE KALENJIN CASE, and the reason this state exists at all.

       Most of the speech at a Marakwet rally is Kalenjin, and Whisper has no model for it.
       Given Kalenjin it does not fail quietly — it picks the nearest language it knows and
       invents fluent words in it; one real recording here came back as Russian. So the
       engine refuses, and this is where the refusal has to be legible, because the
       alternative somebody acts on is a quotation that was never said.
    */
    case 'SKIPPED':
      return (
        <Note tone="info">
          {reason ??
            'The speech was not in a language this transcriber can write down, so no' +
              ' transcript was made.'}{' '}
          The footage is unaffected — it can still be watched, and moments can still be
          marked on it by hand.
        </Note>
      )
    case 'FAILED':
      return (
        <Note tone="warn">
          The transcription failed. The footage is unaffected and the attempt can be made
          again.
        </Note>
      )
    case 'PENDING':
      return (
        <p className="flex items-center gap-2 py-4 text-erp-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Transcribing. An hour of footage takes several minutes.
        </p>
      )
    case 'EMPTY':
      return (
        <Note tone="info">
          The recording was transcribed and no speech was found. On a rally that usually
          means the camera was too far from whoever was talking, or its microphone was
          covered.
        </Note>
      )
    case 'NO_RECORDING':
      return (
        <Note tone="info">
          Nothing was written to disk, so there is nothing to transcribe.
        </Note>
      )
    default:
      return (
        <Note tone="info">
          Nothing has been recorded at this event yet. A transcript is made from a
          recording, so a camera has to go live and be recorded first.
        </Note>
      )
  }
}

/** mm:ss from the start of that camera's own recording. */
function clock(ms: number): string {
  const total = Math.floor(ms / 1000)
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}
